import React, { useState } from 'react';
import { X, Image as ImageIcon, CheckCircle } from 'lucide-react';
import { BlogPost, Category } from '../types';
import { CATEGORIES } from '../constants';
import { createBlogId, createBlogSlug } from '../utils/blogId';

interface CreatePostModalProps {
    onClose: () => void;
    onSubmit: (blog: BlogPost) => void;
}

const CreatePostModal: React.FC<CreatePostModalProps> = ({ onClose, onSubmit }) => {
    const [formData, setFormData] = useState({
        title: '',
        excerpt: '',
        content: '',
        category: 'Wealth' as Category,
        author: 'Admin User',
        readTime: '5 min read',
        imageUrl: 'https://picsum.photos/seed/newpost/800/600',
    });

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        const now = new Date();
        const newBlog: BlogPost = {
            ...formData,
            id: createBlogId(formData.title, now),
            slug: createBlogSlug(formData.title),
            date: now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        };
        onSubmit(newBlog);
    };

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-white w-full max-w-2xl rounded-3xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300">
                <div className="flex justify-between items-center px-8 py-6 border-b border-gray-100">
                    <h2 className="text-2xl font-black text-gray-900">Create New Wealth Blog</h2>
                    <button type="button" onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                        <X size={24} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-8 space-y-6 max-h-[70vh] overflow-y-auto">
                    <div>
                        <label className="block text-sm font-black text-gray-700 uppercase tracking-widest mb-2">Headline</label>
                        <input
                            required
                            type="text"
                            placeholder="e.g. 5 Strategies for Early Retirement"
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                            value={formData.title}
                            onChange={(event) => setFormData({ ...formData, title: event.target.value })}
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-black text-gray-700 uppercase tracking-widest mb-2">Category</label>
                            <select
                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                                value={formData.category}
                                onChange={(event) =>
                                    setFormData({ ...formData, category: event.target.value as Category })
                                }
                            >
                                {CATEGORIES.map((cat) => (
                                    <option key={cat} value={cat}>
                                        {cat}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-black text-gray-700 uppercase tracking-widest mb-2">Read Time</label>
                            <input
                                type="text"
                                placeholder="e.g. 6 min read"
                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all"
                                value={formData.readTime}
                                onChange={(event) => setFormData({ ...formData, readTime: event.target.value })}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-black text-gray-700 uppercase tracking-widest mb-2">Summary</label>
                        <textarea
                            required
                            rows={2}
                            placeholder="A short hook for your readers..."
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all"
                            value={formData.excerpt}
                            onChange={(event) => setFormData({ ...formData, excerpt: event.target.value })}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-black text-gray-700 uppercase tracking-widest mb-2">Full Content</label>
                        <textarea
                            required
                            rows={6}
                            placeholder="Write your wealth advice here..."
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all"
                            value={formData.content}
                            onChange={(event) => setFormData({ ...formData, content: event.target.value })}
                        />
                    </div>

                    <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-100 flex items-center gap-4">
                        <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm text-emerald-600">
                            <ImageIcon size={24} />
                        </div>
                        <div className="flex-grow">
                            <p className="text-sm font-bold text-emerald-900">Cover Image</p>
                            <p className="text-xs text-emerald-700">
                                Using standard high-quality wealth stock image.
                            </p>
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="w-full bg-emerald-600 text-white py-4 rounded-xl font-black text-lg shadow-lg hover:bg-emerald-700 transition-all flex items-center justify-center gap-2 active:scale-[0.98]"
                    >
                        <CheckCircle size={22} /> Publish to Firerify Blog
                    </button>
                </form>
            </div>
        </div>
    );
};

export default CreatePostModal;
